import React from "react";
import styled, { keyframes } from "styled-components";
import { colors } from "../theme/colors";

const Loader = ({
  content = "Loading...",
  subtext = "Please wait while we fetch your information",
  size = "md",
  centered = true,
  className,
  ...props
}) => {
  const sizeMap = {
    sm: 60,
    md: 80,
    lg: 100,
  };

  const spinnerSize = sizeMap[size] || sizeMap.md;

  return (
    <LoadingContainer className={className} centered={centered}>
      <SpinnerWrapper size={spinnerSize}>
        <Spinner size={spinnerSize} />
        <SpinnerInner size={spinnerSize} />
      </SpinnerWrapper>
      <LoadingText size={size}>{content}</LoadingText>
      {subtext && <LoadingSubtext size={size}>{subtext}</LoadingSubtext>}
    </LoadingContainer>
  );
};

// Animations
const spin = keyframes`
   0% {
      transform: rotate(0deg);
   }
   100% {
      transform: rotate(360deg);
   }
`;

const pulse = keyframes`
   0%, 100% {
      opacity: 1;
   }
   50% {
      opacity: 0.5;
   }
`;

// Styled Components
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ centered }) => (centered ? "center" : "flex-start")};
  min-height: ${({ centered }) => (centered ? "400px" : "200px")};
  padding: 40px 20px;
  width: 100%;
`;

const SpinnerWrapper = styled.div`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  margin-bottom: 24px;
`;

const Spinner = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid rgba(44, 90, 160, 0.1);
  border-top: 4px solid ${colors.primary.main};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const SpinnerInner = styled.div`
  position: absolute;
  top: ${({ size }) => size * 0.125}px;
  left: ${({ size }) => size * 0.125}px;
  right: ${({ size }) => size * 0.125}px;
  bottom: ${({ size }) => size * 0.125}px;
  border: 4px solid transparent;
  border-top: 4px solid ${colors.primary.light};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite reverse;
`;

const LoadingText = styled.h2`
  font-size: ${({ size }) => {
    switch (size) {
      case "sm":
        return "18px";
      case "lg":
        return "28px";
      default:
        return "24px";
    }
  }};
  font-weight: 600;
  color: ${colors.neutral[700]};
  margin: 0 0 8px 0;
  animation: ${pulse} 2s ease-in-out infinite;

  @media screen and (max-width: 768px) {
    font-size: ${({ size }) => {
      switch (size) {
        case "sm":
          return "16px";
        case "lg":
          return "24px";
        default:
          return "20px";
      }
    }};
  }
`;

const LoadingSubtext = styled.p`
  font-size: ${({ size }) => {
    switch (size) {
      case "sm":
        return "12px";
      case "lg":
        return "16px";
      default:
        return "14px";
    }
  }};
  color: ${colors.neutral[500]};
  margin: 0;
  font-weight: 400;
  text-align: center;
  max-width: 400px;

  @media screen and (max-width: 768px) {
    font-size: ${({ size }) => {
      switch (size) {
        case "sm":
          return "11px";
        case "lg":
          return "15px";
        default:
          return "13px";
      }
    }};
  }
`;

export default Loader;
